import React, { useState, useMemo, useEffect, useRef } from "react";
import { compareSchoolYearDesc } from "../../../util/schoolYear";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import LeftMenuSidebar from "../../../unicomponents/LeftMenuSidebar";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { useUIContext } from "../../../context/UIContext";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import useStudentInfo from "../../../hooks/useStudentInfo";
import useBilling from "../../../hooks/useBilling";
import DropdownSchoolYear from "../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownHierarchyOne from "../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import DropdownHierarchyTwo from "../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyThree from "../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import styles from "./css/BillingManagement.module.css";
import BillingInputPage from "./components/input/BillingInputPage";
import BillingListPage from "./components/list/BillingListPage";
import BillingSettingsPage from "./components/settings/BillingSettingsPage";
import BillingPrintPage from "./components/print/BillingPrintPage";
import HelpHyperLink from "../../../unicomponents/HelpHyperLink";
import {
  billingRecordMatch,
  filterSessionsForCourse,
  sessionOptionLabel,
} from "../../../util/billing/normalizeBillingEntities";

export default function BillingManagementMain() {
  const { t } = useTranslation();
  const location = useLocation();
  const schoolId = location.pathname.split("/")[1];
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuthContext();
  const { handleToastCenterTop } = useUIContext();
  const prevMenuIdRef = useRef(null);

  const [activeMenu, setActiveMenu] = useState({
    menu_name: "input",
    menu_id: "input",
  });

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    registrationInfoQuery: { data: registrationInfoData },
  } = useRegistrationInfo({ schoolid: schoolId });
  const {
    studentInfoQuery: { data: studentInfoData },
  } = useStudentInfo({ schoolid: schoolId });

  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
  });

  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedPrintUsername, setSelectedPrintUsername] = useState("");
  const [paymentMethodOverrides, setPaymentMethodOverrides] = useState({});

  const {
    bootstrapQuery,
    upsertOneMutation,
    upsertBulkMutation,
    createSessionMutation,
    patchSessionMutation,
    deleteSessionMutation,
    reorderSessionsMutation,
    createSessionItemMutation,
    syncSessionItemsMutation,
    patchSessionItemMutation,
    deleteSessionItemMutation,
    reorderSessionItemsMutation,
    fetchSessionItems,
  } = useBilling({
    schoolid: schoolId,
    schoolYearId: filters.schoolYearId,
  });

  const [sessions, setSessions] = useState([]);
  const [sessionItems, setSessionItems] = useState([]);
  const [billingRecords, setBillingRecords] = useState([]);

  /** 권한 매칭: 그룹 메뉴의 `link`가 `/billing-management`와 일치해야 함(시드/DB 확인). */
  const canRead = hasPermission(null, "can_read");
  const canCreate = hasPermission(null, "can_create");
  const canUpdate = hasPermission(null, "can_update");
  const canDelete = hasPermission(null, "can_delete");

  const billingMenus = useMemo(
    () => [
      {
        id: "input",
        menu_name: "input",
        menu_label: t("billingManagement.menus.input") || "입력",
      },
      {
        id: "list",
        menu_name: "list",
        menu_label: t("billingManagement.menus.list") || "조회",
      },
      {
        id: "settings",
        menu_name: "settings",
        menu_label: t("billingManagement.menus.settings") || "설정",
      },
      {
        id: "print",
        menu_name: "print",
        menu_label: t("billingManagement.menus.print") || "출력",
      },
    ],
    [t],
  );

  const visibleBillingMenus = useMemo(
    () =>
      canRead ? billingMenus : billingMenus.filter((m) => m.id === "input"),
    [canRead, billingMenus],
  );

  useEffect(() => {
    const allowed = visibleBillingMenus.some(
      (m) => m.id === activeMenu.menu_id,
    );
    if (!allowed) {
      setActiveMenu({ menu_name: "input", menu_id: "input" });
    }
  }, [visibleBillingMenus, activeMenu.menu_id]);

  useEffect(() => {
    if (filters.schoolYear !== null || !settingData || settingData.length === 0)
      return;
    const sorted = [...settingData]
      .filter((r) => r.hierarchy_level === 0 && r.parent_id === 0)
      .sort((a, b) => compareSchoolYearDesc(a.school_year, b.school_year));
    if (sorted.length === 0) return;
    const initial = sorted[0];
    setFilters((prev) => ({
      ...prev,
      schoolYear: initial.school_year,
      schoolYearId: String(initial.id),
    }));
  }, [settingData, filters.schoolYear]);

  useEffect(() => {
    const d = bootstrapQuery.data;
    if (!d) return;
    setSessions(d.sessions || []);
    setSessionItems(d.sessionItems || []);
    const recs = d.billingRecords || [];
    setBillingRecords(recs);
  }, [bootstrapQuery.data]);

  useEffect(() => {
    if (bootstrapQuery.isError) {
      handleToastCenterTop(
        t("billingManagement.messages.loadFailed", {
          detail: bootstrapQuery.error?.message || "",
        }),
      );
    }
  }, [bootstrapQuery.isError, bootstrapQuery.error, handleToastCenterTop, t]);

  /** 설정·다른 탭에서 항목/세션 저장 후 입력 탭으로 올 때 캐시된 bootstrap에 항목이 비어 있는 문제 방지 */
  useEffect(() => {
    const prev = prevMenuIdRef.current;
    prevMenuIdRef.current = activeMenu.menu_id;
    if (prev === null) return;
    if (activeMenu.menu_id !== "input" || prev === "input") return;
    if (!schoolId || !filters.schoolYearId) return;
    queryClient.invalidateQueries({
      queryKey: ["billingBootstrap", schoolId, filters.schoolYearId],
    });
  }, [activeMenu.menu_id, schoolId, filters.schoolYearId, queryClient]);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;

    if (name === "0") {
      setFilters({
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "",
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      });
      setSelectedSessionId("");
      setSelectedPrintUsername("");
    }
    if (name === "1") {
      setFilters((prev) => ({
        ...prev,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      }));
      setSelectedSessionId("");
      setSelectedPrintUsername("");
    }
    if (name === "2") {
      setFilters((prev) => ({
        ...prev,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
      }));
    }
    if (name === "3") {
      setFilters((prev) => ({ ...prev, hierarchyThreeId: value }));
    }
  };

  const selectedCourseName = useMemo(() => {
    if (
      !filters.hierarchyOneId ||
      filters.hierarchyOneId === "0" ||
      !settingData
    )
      return "";
    const s = settingData.find(
      (s) => String(s.id) === String(filters.hierarchyOneId),
    );
    return s?.group_name || "";
  }, [filters.hierarchyOneId, settingData]);

  /** readallWithUsers: user 행 + nested studentinfos[] — PK·ex_six는 studentinfos[0] 기준 */
  const studentInfoByUsername = useMemo(() => {
    if (!studentInfoData) return {};
    const map = {};
    studentInfoData.forEach((row) => {
      if (row?.username) {
        map[row.username] = row;
      }
    });
    return map;
  }, [studentInfoData]);

  const resolvedStudentList = useMemo(() => {
    if (!registrationInfoData || !settingData) return [];

    let filtered = registrationInfoData;
    if (filters.schoolYear) {
      filtered = filtered.filter(
        (r) => String(r.school_year) === String(filters.schoolYear),
      );
    }
    if (filters.hierarchyOneId && filters.hierarchyOneId !== "0") {
      filtered = filtered.filter((r) =>
        r.organization_path?.includes("/" + filters.hierarchyOneId),
      );
    }
    if (filters.hierarchyTwoId && filters.hierarchyTwoId !== "0") {
      filtered = filtered.filter((r) =>
        r.organization_path?.includes("/" + filters.hierarchyTwoId),
      );
    }
    if (filters.hierarchyThreeId && filters.hierarchyThreeId !== "0") {
      filtered = filtered.filter((r) =>
        r.organization_path?.includes("/" + filters.hierarchyThreeId),
      );
    }
    return filtered
      .map((reg) => {
        const pathIds =
          reg.organization_path?.split("/").filter((p) => p) || [];
        const resolved = {};
        pathIds.forEach((id, index) => {
          const setting = settingData.find((s) => s.id === Number(id));
          if (setting) resolved[index] = setting.group_name;
        });
        const userRow = studentInfoByUsername[reg.username];
        const studentInfoRow =
          userRow?.studentinfos?.[0] ?? userRow?.studentInfos?.[0];
        return {
          username: reg.username,
          fullname: reg.user?.fullname || "",
          school_year: reg.school_year,
          organization_path: reg.organization_path,
          hierarchyOne: resolved[1] || "",
          hierarchyTwo: resolved[2] || "",
          hierarchyThree: resolved[3] || "",
          registration_id: reg.id,
          studentinfo_id: studentInfoRow?.id ?? null,
          payment_method:
            paymentMethodOverrides[reg.username] ??
            studentInfoRow?.ex_six ??
            "",
        };
      })
      .sort((a, b) => {
        if (a.hierarchyOne !== b.hierarchyOne)
          return a.hierarchyOne.localeCompare(b.hierarchyOne);
        if (a.hierarchyTwo !== b.hierarchyTwo)
          return String(a.hierarchyTwo).localeCompare(String(b.hierarchyTwo));
        if (a.hierarchyThree !== b.hierarchyThree)
          return String(a.hierarchyThree).localeCompare(
            String(b.hierarchyThree),
          );
        return a.fullname.localeCompare(b.fullname);
      });
  }, [
    registrationInfoData,
    settingData,
    filters,
    paymentMethodOverrides,
    studentInfoByUsername,
  ]);

  const filteredSessionOptions = useMemo(
    () =>
      filterSessionsForCourse(
        sessions,
        filters.schoolYearId,
        filters.hierarchyOneId,
      ),
    [sessions, filters.schoolYearId, filters.hierarchyOneId],
  );

  const currentSessionItems = useMemo(
    () =>
      sessionItems
        .filter(
          (si) =>
            si.billing_session_id != null &&
            String(si.billing_session_id) === String(selectedSessionId),
        )
        .sort(
          (a, b) =>
            (Number(a.display_order) || 0) - (Number(b.display_order) || 0),
        ),
    [sessionItems, selectedSessionId],
  );
  const currentSession = sessions.find(
    (s) => String(s.id) === String(selectedSessionId),
  );

  const getBillingRecord = (sessionId, username, itemId) =>
    billingRecords.find((r) =>
      billingRecordMatch(r, sessionId, username, itemId),
    );

  const updateBillingRecord = (sessionId, username, itemId, data) => {
    setBillingRecords((prev) => {
      const idx = prev.findIndex((r) =>
        billingRecordMatch(r, sessionId, username, itemId),
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...data };
        return updated;
      }
      return [
        ...prev,
        {
          session_id: String(sessionId),
          username,
          item_id: String(itemId),
          amount: null,
          bill_remark: "",
          internal_memo: "",
          ...data,
        },
      ];
    });
    upsertOneMutation.mutate({
      sessionId,
      username,
      itemId,
      amount: data.amount,
      billRemark: data.bill_remark,
      internalMemo: data.internal_memo,
    });
  };

  const updateBillingRecordsBulk = (records) => {
    if (!records?.length) return;
    const sessionId = records[0].session_id;
    setBillingRecords((prev) => {
      const next = [...prev];
      records.forEach((rec) => {
        const idx = next.findIndex((r) =>
          billingRecordMatch(r, rec.session_id, rec.username, rec.item_id),
        );
        if (idx >= 0) {
          next[idx] = { ...next[idx], ...rec };
        } else {
          next.push({
            amount: null,
            bill_remark: "",
            internal_memo: "",
            ...rec,
          });
        }
      });
      return next;
    });
    upsertBulkMutation.mutate({
      sessionId,
      records: records.map((r) => ({
        username: r.username,
        itemId: r.item_id,
        amount: r.amount,
        bill_remark: r.bill_remark,
        internal_memo: r.internal_memo,
      })),
    });
  };

  const sharedProps = {
    schoolId,
    user,
    schoolYear: filters.schoolYear,
    schoolYearId: filters.schoolYearId,
    selectedSessionId,
    setSelectedSessionId,
    sessions,
    currentSession,
    currentSessionItems,
    sessionItems,
    setSessionItems,
    resolvedStudentList,
    billingRecords,
    setBillingRecords,
    getBillingRecord,
    updateBillingRecord,
    updateBillingRecordsBulk,
    handleToastCenterTop,
    activeMenu,
    setActiveMenu,
    settingData,
    selectedPrintUsername,
    setSelectedPrintUsername,
    selectedCourseName,
    setPaymentMethodOverrides,
    selectedClassId: filters.hierarchyThreeId,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
  };

  const settingsBillingApi = {
    schoolId,
    schoolYearId: filters.schoolYearId,
    createSessionMutation,
    patchSessionMutation,
    deleteSessionMutation,
    reorderSessionsMutation,
    createSessionItemMutation,
    syncSessionItemsMutation,
    patchSessionItemMutation,
    deleteSessionItemMutation,
    reorderSessionItemsMutation,
    fetchSessionItems,
    handleToastCenterTop,
  };

  if (!hasPermission(null, "can_access")) {
    return (
      <SimpleAlertMessageBlock
        message={
          t("common.messages.noAccessPermission") || "접근 권한이 없습니다."
        }
      />
    );
  }

  return (
    <div className={`basicWidth ${styles.pageContainer}`}>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-2">
            <LeftMenuSidebar
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              menuData={visibleBillingMenus}
              itemName="menu_name"
              itemLabel="menu_label"
            />
          </div>
          <div className="col-12 col-md-10 mt-3">
            <div className={styles.contentArea}>
              {activeMenu.menu_id !== "settings" && (
                <div className={styles.filterRow}>
                  <div style={{ width: "95px" }}>
                    <DropdownSchoolYear
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYearId={filters.schoolYearId}
                      allYears={false}
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyOne
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyOneId={filters.hierarchyOneId}
                      hierarchyOneTitle={
                        t("common.labels.schoolStage") || "과정"
                      }
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyTwo
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyOneId={filters.hierarchyOneId}
                      hierarchyTwoId={filters.hierarchyTwoId}
                      hierarchyTwoTitle={
                        t("common.labels.studentYear") || "학년"
                      }
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyThree
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyTwoId={filters.hierarchyTwoId}
                      hierarchyThreeId={filters.hierarchyThreeId}
                      hierarchyThreeTitle={t("common.labels.class")}
                    />
                  </div>
                  {activeMenu.menu_id === "print" && (
                    <select
                      className="form-control form-control-sm"
                      value={selectedPrintUsername}
                      onChange={(e) => setSelectedPrintUsername(e.target.value)}
                      style={{ maxWidth: 140 }}
                    >
                      <option value="">
                        {t("billingManagement.filters.selectStudent") ||
                          "학생선택"}
                      </option>
                      {resolvedStudentList.map((s) => (
                        <option key={s.username} value={s.username}>
                          {s.fullname}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    className="form-control form-control-sm"
                    value={selectedSessionId}
                    onChange={(e) => {
                      setSelectedSessionId(e.target.value);
                    }}
                    style={{ maxWidth: 120 }}
                  >
                    <option value="">
                      {t("billingManagement.filters.selectPeriod") ||
                        "기간 선택"}
                    </option>
                    {selectedCourseName &&
                      filteredSessionOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {sessionOptionLabel(s)}
                        </option>
                      ))}
                  </select>
                  <HelpHyperLink link={"https://youtu.be/sqoGz-S34P0"} />
                </div>
              )}

              {activeMenu.menu_id === "input" && (
                <BillingInputPage {...sharedProps} />
              )}
              {activeMenu.menu_id === "list" && (
                <BillingListPage {...sharedProps} />
              )}
              {activeMenu.menu_id === "settings" && (
                <BillingSettingsPage
                  schoolId={schoolId}
                  sessions={sessions}
                  setSessions={setSessions}
                  sessionItems={sessionItems}
                  setSessionItems={setSessionItems}
                  settingData={settingData}
                  handleToastCenterTop={handleToastCenterTop}
                  schoolYear={filters.schoolYear}
                  schoolYearId={filters.schoolYearId}
                  billingApi={settingsBillingApi}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              )}
              {activeMenu.menu_id === "print" && (
                <BillingPrintPage {...sharedProps} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
